import { Either, List, Validation } from 'monet';
import LeftResponse from '@/storage/LeftResponse';

/**
 * @description Префикс имени шаблона.
 * @type {string}
 */
const PREFIX_NAME = 'ОБРАЗЕЦ';

/**
 * @description Прикрепленный документ (вложенный в пакет)
 * @param {number} id - ID документа
 * @param {number} templateId - ID шаблона документа
 * @param {string} name - Название документа
 * @param {boolean} isContract - Это договор?
 * @param {boolean} isNeedContractNumber - Требуется номер договора?
 * @param {string} [number] - Номер договора (для контрактов)
 * @param {'contract'|'document'} type - Тип документа
 * @param {Date} createdDate - Дата создания
 * @constructor
 */
export function AttachedDocument(
  id,
  templateId,
  name,
  isContract,
  isNeedContractNumber,
  number,
  type,
  createdDate,
) {
  this.id = id;
  this.templateId = templateId;
  this.name = name;
  this.isContract = isContract;
  this.isNeedContractNumber = isNeedContractNumber;
  this.number = number || null;
  this.type = type;
  this.createdDate = createdDate;

  /**
   * @description Установить номер договора для Документа.
   * @returns {number}
   */
  this.setContractNumberForDocument = (contractNumber) => { this.number = contractNumber; };

  this.toJson = () => ({
    id: this.id,
    template_id: this.templateId,
    name: this.name,
    is_contract: this.isContract,
    is_need_contract_number: this.isNeedContractNumber,
    number: this.number,
    type: this.type,
    created_date: this.createdDate,
  });
}

/**
 * @description Шаблон пациента (документ, договор или пакет)
 * @param {number} id - ID шаблона пациента
 * @param {string} name - Название документа/пакета
 * @param {boolean} isContract - Это договор?
 * @param {'contract'|'document'|'package'} type - Тип: договор, документ или пакет
 * @param {string} createdDate - Дата создания в формате "дд.мм.гггг"
 * @param {number} templateId - ID шаблона (для генерации документов)
 * @param {string} [number] - Номер договора (только для isContract = true)
 * @param {boolean} isSent - Отправлен ли на подпись?
 * @param {boolean} isStatusFinal - Финальный ли статус?
 * @param {string} status - Текстовый статус
 * @param {boolean} [packageNeedContractNumber] - Устаревшее поле
 * @param {string} [typeName] - Название типа
 * @param {boolean} isHasNumberContractVariable - Имеет ли переменную номера договора?
 * @param {AttachedDocument[]} [attachedDocuments] - Вложенные документы (для пакетов)
 * @constructor
 */
export function PatientTemplate(
  id,
  name,
  isContract,
  type,
  createdDate,
  templateId,
  number,
  isSent,
  isStatusFinal,
  status,
  packageNeedContractNumber,
  typeName,
  isHasNumberContractVariable,
  attachedDocuments,
) {
  this.id = id;
  this.name = name;
  this.isContract = isContract;
  this.type = type;
  this.createdDate = createdDate;
  this.templateId = templateId;
  this.number = number || '';
  this.isSent = isSent;
  this.isStatusFinal = isStatusFinal;
  this.status = status;
  this.packageNeedContractNumber = packageNeedContractNumber || false;
  this.typeName = typeName || '';
  this.isHasNumberContractVariable = isHasNumberContractVariable;
  this.attachedDocuments = (attachedDocuments || []).map((doc) => new AttachedDocument(
    doc.id,
    doc.templateId,
    doc.name,
    doc.isContract,
    doc.isNeedContractNumber,
    doc.number,
    doc.type,
    doc.createdDate,
  ));

  /**
   * Проверяет, что все контракты и документы, требующие номер договора,
   * имеют заполненное поле number.
   * @returns {boolean}
   */
  this.hasAllContractNumbersFilled = function hasAllContractNumbersFilled() {
    let allFilled = true;
    this.attachedDocuments.forEach((document) => {
      if (document.isContract) {
        const numberStr = document.number ? String(document.number).trim() : '';
        if (numberStr === '' || !/^[\d\-./]+$/.test(numberStr)) {
          allFilled = false;
        }
      }
      if (!document.isContract && document.isNeedContractNumber && document.number === null) {
        allFilled = false;
      }
    });
    return allFilled;
  };

  /**
   * @description Конвертирует PatientTemplate в JSON для отправки на сервер.
   * @returns {Object}
   */
  this.toJSON = function toJSON() {
    return {
      id: this.id,
      name: this.name,
      is_contract: this.isContract,
      type: this.type,
      created_date: this.createdDate,
      template_id: this.templateId,
      number: this.number || null,
      is_sent: this.isSent,
      is_status_final: this.isStatusFinal,
      status: this.status,
      package_need_contract_number: this.packageNeedContractNumber,
      type_name: this.typeName,
      is_has_number_contract_variable: this.isHasNumberContractVariable,
      attached_documents: this.attachedDocuments.map((doc) => doc.toJson()),
    };
  };
}

/**
 * @description Запрос на проверку шаблона клиники.
 * @param {number} id
 * @param {File} file
 * @constructor
 */
export function ClinicTemplateRequestPreview(id, file) {
  this.id = id;
  this.file = file;

  /**
   * @description К запросу.
   * @returns {{template: *, name}}
   */
  this.toQuery = () => {
    const data = new FormData();
    data.append('template_id', this.id);
    if (this.file && this.file.size) {
      data.append('template', this.file);
    }
    data.append('_method', 'POST');
    return data;
  };
}

/**
 * @description Запрос на редактирование шаблона клиники.
 * @param {number} id
 * @param {string} name
 * @param {File} file
 * @param {string} categoryId
 * @param {boolean} isEsiaConfirmationRequired
 * @constructor
 */
export function ClinicTemplateRequestEdit(
  id,
  name,
  file,
  categoryId,
  isEsiaConfirmationRequired,
) {
  this.id = id;
  this.name = name;
  this.file = file;
  this.categoryId = categoryId;
  this.isEsiaConfirmationRequired = isEsiaConfirmationRequired;
  /**
   * @description К запросу.
   * @returns {{template: *, name}}
   */
  this.toQuery = () => {
    const data = new FormData();
    data.append('name', this.name);
    data.append('category_id', this.categoryId);
    this.categoryId = categoryId;
    if (this.file && this.file.size) {
      data.append('template', this.file);
    }
    data.append('is_esia_confirmation_required', this.isEsiaConfirmationRequired ? 1 : 0);
    data.append('_method', 'PUT');
    return data;
  };

  /**
   * @description Получить Id шаблона.
   * @returns {number}
   */
  this.getTemplateId = () => this.id;

  /**
   * @description Получить Id категории.
   * @returns {string|*}
   */
  this.getCategoryId = () => this.categoryId;
}

/**
 * @description Запрос на редактирование системного шаблона клиники.
 * @param {number|null} id
 * @param {string} name
 * @param {File|null} file
 * @param {string} type
 * @constructor
 */
export function ClinicSystemTemplateRequestEdit(id, name, file, type) {
  this.id = id;
  this.name = name;
  this.file = file;
  this.type = type;

  /**
   * @description К запросу.
   * @returns {{template: *, name}}
   */
  this.toQuery = () => {
    const data = new FormData();
    data.append('id', this.id ?? '');
    data.append('name', this.name);
    data.append('type', this.type);
    if (this.file && this.file.size) {
      data.append('template', this.file);
    }
    data.append('_method', 'PUT');
    return data;
  };
}

/**
 * @description Запрос на создание шаблона клиники.
 * @param {string} name
 * @param {File} file
 * @param {string} categoryId
 * @param {boolean} isEsiaConfirmationRequired
 * @constructor
 */
export function ClinicTemplateRequestCreate(
  name,
  file,
  categoryId,
  isEsiaConfirmationRequired,
) {
  this.name = name;
  this.file = file;
  this.categoryId = categoryId;
  this.isEsiaConfirmationRequired = isEsiaConfirmationRequired;
  /**
   * @description К запросу.
   * @returns {{template: *, name}}
   */
  this.toQuery = () => {
    const data = new FormData();
    data.append('name', this.name);
    data.append('category_id', this.categoryId);
    if (this.file && this.file.size) {
      data.append('template', this.file);
    }
    data.append('is_esia_confirmation_required', this.isEsiaConfirmationRequired ? 1 : 0);
    data.append('_method', 'POST');
    return data;
  };

  /**
   * @description Получить Id категории.
   * @returns {string|*}
   */
  this.getCategoryId = () => this.categoryId;
}

/**
 * @description Шаблон
 @param {string[]} allowedActions Допустимые действия.
 @param {number} id               Id
 @param {boolean} isSample        Образец ли
 @param {string} name             Имя
 @param {string} removingId       Идентификатор для удаления.
 @param {string} type             Тип
 @param {string} typeName         Имя типа
 @param {string} fileName         Имя типа
 @param {string} categoryId       Id Категории
 @param {boolean} isEsiaConfirmationRequired Требуется ли подтверждение подписи через ЕСИА? (F.doc)
 * @constructor
 */
function Template(
  allowedActions,
  id,
  isSample,
  name,
  removingId,
  type,
  typeName,
  fileName,
  categoryId,
  isEsiaConfirmationRequired,
) {
  this.id = id;
  this.isCanBeRemoved = allowedActions.includes('remove');
  this.isSample = isSample;
  this.name = name;
  this.removingId = removingId;
  this.type = type;
  this.typeName = typeName;
  this.fileName = fileName;
  this.categoryId = categoryId;
  this.isEsiaConfirmationRequired = isEsiaConfirmationRequired;

  /**
   * @description Получить имя с префиксом.
   * @returns {string}
   */
  this.getNameWithPrefix = () => (this.isSample ? `${PREFIX_NAME} ${this.name}` : this.name);
}

/**
 * @description Выбранный шаблон документа/контракта для шаблонного Пакета документов/контрактов
 *
 * @param {string} id   Идентификатор шаблона
 * @param {string} type Тип шаблона (документ/контракт)
 * @param {string} name Наименование шаблона
 * @param {bool} isSample Флаг. Является ли Образцом
 * @constructor
 */
export function SelectTemplate(
  id,
  type,
  name,
  isSample,
) {
  this.id = id;
  this.type = type;
  this.name = name;
  this.isSample = isSample;
}

/**
 * @description Категория с шаблонами
 *
 * @param {string}     categoryId   ID категории
 * @param {string}     categoryName Название категории
 * @param {Template[]} templates    Список шаблонов категории
 * @constructor
 */
function CategoryWithTemplates(
  categoryId,
  categoryName,
  templates,
) {
  this.categoryId = categoryId;
  this.categoryName = categoryName;
  this.templates = templates;
}

/**
 * @description Вложенный в шаблонный Пакет клиники документ.
 * (Может быть как Контрактом так и Документом)
 *
 * @param {number} id идентификатор Пакета
 * @param {string} name наименование Пакета
 * @param {string} type наименование Пакета
 * @param {boolean} isSample наименование Пакета
 * @constructor
 */
export function InternalDocument(
  id,
  name,
  type,
  isSample,
) {
  this.id = id;
  this.name = name;
  this.type = type;
  this.isSample = isSample;
}

/**
 * @description Шаблонный Пакет документов/контрактов
 *
 * @param {number} id идентификатор Пакета
 * @param {string} name наименование Пакета
 * @param {List<InternalDocument>} internalDocuments список вложенных документов Пакета
 * @constructor
 */
export function Package(
  id,
  name,
  internalDocuments,
) {
  this.id = id;
  this.name = name;
  this.internalDocuments = internalDocuments;
}

/**
 * @description Запрос на создание категории документов.
 * @param name
 * @constructor
 */
export function DocumentCategoryCreateRequest(name) {
  /**
   * @description К запросу.
   * @returns {{name}}
   */
  this.toQuery = () => ({
    name,
  });
}

/**
 * @description Запрос на создание категории документов.
 * @param id
 * @param name
 * @constructor
 */
export function DocumentCategoryEditRequest(id, name) {
  /**
   * @description К запросу.
   * @returns {{name}}
   */
  this.toQuery = () => ({
    name,
  });
  this.getId = () => id;
}

/**
 * @description Шлюз шаблонов документов.
 * @param {Axios} axios
 */
export default function DocumentTemplateGateway(axios) {
  /**
   * @description Добавить пациенту шаблон.
   * @param {string} patientId
   * @param {string} templateId
   * @param {string} templateType
   * @param {boolean} showOnAllBranches
   * @returns {Promise<Either>}
   */
  this.addPatientTemplate = (
    patientId,
    templateId,
    templateType,
    showOnAllBranches,
  ) => axios
    .post(
      `/api/patient/${patientId}/template/${templateId}`,
      { template_type: templateType, show_on_all_branches: showOnAllBranches },
    ).then(
      (response) => Either.of(response.data),
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
            || error.response.status === 403
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Добавить пациенту шаблон по образцу.
   * @param {string} patientId
   * @param {string} sampleId
   * @param {string} templateType
   * @param {boolean} showOnAllBranches
   * @returns {Promise<Either>}
   */
  this.addPatientTemplateBySample = (
    patientId,
    sampleId,
    templateType,
    showOnAllBranches,
  ) => axios
    .post(
      `/api/patient/${patientId}/sample/${sampleId}`,
      { template_type: templateType, show_on_all_branches: showOnAllBranches },
    ).then(
      (response) => Either.of(response.data),
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
            || error.response.status === 403
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Добавить пациенту шаблон Пакета.
   *
   * @param {string} patientId
   * @param {string} packageId
   * @param {string} templateType
   * @param {boolean} showOnAllBranches
   * @returns {Promise<Either>}
   */
  this.addPatientPackage = (
    patientId,
    packageId,
    templateType,
    showOnAllBranches,
  ) => axios
    .post(
      `/api/patient/${patientId}/package/${packageId}`,
      { show_on_all_branches: showOnAllBranches },
    ).then(
      (response) => Either.of(response.data),
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
            || error.response.status === 403
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Сохранить шаблон документа клиники.
   * @param {ClinicTemplateRequestEdit} request
   * @returns {Promise<Either>}
   */
  this.editClinicDocumentTemplate = (request) => axios
    .post(`/api/templates/document/${request.getTemplateId()}`, request.toQuery())
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Сохранить отредактированный шаблонный Пакет документов клиники.
   * @param selectedTemplatesFromEditPackage
   * @param selectedPackageId
   * @returns {Promise<Either>}
   */
  this.editClinicPackageDocumentTemplate = (
    selectedTemplatesFromEditPackage,
    selectedPackageId,
  ) => axios
    .put(`/api/templates/package/${selectedPackageId}`, selectedTemplatesFromEditPackage)
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Сохранить шаблон документа клиники.
   * @param {ClinicSystemTemplateRequestEdit} request
   * @returns {Promise<Either>}
   */
  this.editSystemClinicDocumentTemplate = (request) => axios
    .post('/api/templates/system-document', request.toQuery())
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Предпросмотр шаблон документа клиники.
   * @param {ClinicTemplateRequestPreview} request
   * @returns {Promise<Either>}
   */
  this.previewClinicDocumentTemplate = (request) => axios
    .post(
      '/api/templates/document/preview',
      request.toQuery(),
      {
        headers: { accept: 'application/pdf' },
        responseType: 'blob',
      },
    )
    .then(
      (response) => Either.of(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const json = await (error.response.data || '').text();
          const data = JSON.parse(json);
          const { causes, title } = data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Предпросмотр образца шаблона документа.
   * @param {Number} sampleId
   * @param {Number} type
   * @returns {Promise<Either>}
   */
  this.previewSampleTemplate = (sampleId, type) => axios
    .get(
      `/api/samples/preview/${sampleId}?document_type=${type}`,
      {
        headers: { accept: 'application/pdf' },
        responseType: 'blob',
      },
    )
    .then(
      (response) => Either.of(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const json = await (error.response.data || '').text();
          const data = JSON.parse(json);
          const { causes, title } = data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Сохранить шаблон документа клиники.
   * @returns {Promise<Either>}
   */
  this.downloadTemplateVariables = () => axios
    .get(
      '/api/templates/variables/download',
      {
        responseType: 'blob',
      },
    )
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Сохранить шаблон документа клиники.
   * @returns {Promise<Either>}
   */
  this.downloadSystemTemplateVariables = (type) => axios
    .get(
      '/api/templates/system-document/variables/download',
      {
        responseType: 'blob',
        params: {
          type,
        },
      },
    )
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Сохранить шаблон документа клиники.
   * @returns {Promise<Either>}
   */
  this.downloadSystemTemplate = (type) => axios
    .get(
      '/api/templates/system-document/template/download',
      {
        responseType: 'blob',
        params: {
          type,
        },
      },
    )
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Предпросмотр шаблона документа клиники.
   * @param {ClinicTemplateRequestPreview} request
   * @returns {Promise<Either>}
   */
  this.previewClinicContractTemplate = (request) => axios
    .post(
      '/api/templates/contract/preview',
      request.toQuery(),
      {
        headers: { accept: 'application/pdf' },
        responseType: 'blob',
      },
    )
    .then(
      (response) => Either.of(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const json = await (error.response.data || '').text();
          const data = JSON.parse(json);
          const { causes, title } = data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Загрузить шаблон документа клиники.
   * @param {String} templateId
   * @returns {Promise<Either>}
   */
  this.downloadClinicDocumentTemplate = (templateId) => axios
    .get(
      `/api/templates/document/download/${templateId}`,
      {
        responseType: 'blob',
      },
    )
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Загрузить системный шаблон документа клиники.
   * @param {String} templateId
   * @returns {Promise<Either>}
   */
  this.downloadClinicSystemDocumentTemplate = (templateId) => axios
    .get(
      `/api/templates/system-document/download/${templateId}`,
      {
        responseType: 'blob',
      },
    )
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Загрузить шаблон договора клиники.
   * @param {String} templateId
   * @returns {Promise<Either>}
   */
  this.downloadClinicContractTemplate = (templateId) => axios
    .get(
      `/api/templates/contract/download/${templateId}`,
      {
        responseType: 'blob',
      },
    )
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Загрузить образец шаблона.
   * @param {String} sampleId
   * @param {String} type
   * @returns {Promise<Either>}
   */
  this.downloadSampleTemplate = (sampleId, type) => axios
    .get(
      `/api/samples/download/${sampleId}?document_type=${type}`,
      {
        responseType: 'blob',
      },
    )
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Сохранить шаблон документа клиники.
   * @param {ClinicTemplateRequestEdit} request
   * @returns {Promise<Either>}
   */
  this.editClinicContractTemplate = (request) => axios
    .post(`/api/templates/contract/${request.getTemplateId()}`, request.toQuery())
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Сохранить шаблон документа клиники.
   * @param {ClinicTemplateRequestCreate} request
   * @returns {Promise<Either>}
   */
  this.storeClinicDocumentTemplate = (request) => axios
    .post('/api/templates/document', request.toQuery())
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Сохранить шаблон договора клиники.
   * @param {ClinicTemplateRequestCreate} request
   * @returns {Promise<Either>}
   */
  this.storeClinicContractTemplate = (request) => axios
    .post('/api/templates/contract', request.toQuery())
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Удалить документ у пациента.
   *
   * @param {string} patientId
   * @param {string} templateId
   * @param {string} type
   * @returns {*}
   */
  this.deletePatientTemplate = (patientId, templateId, type) => axios
    .delete(`/api/patient/${patientId}/template/${templateId}`, { data: { type } })
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
            || error.response.status === 403
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Удалить шаблон.
   * @param {string} templateId Идентификатор шаблона.
   * @returns {Promise<Either>}
   */
  this.deleteTemplate = (templateId) => axios
    .delete(`/api/document-templates/${templateId}`)
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
            || error.response.status === 404
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получить список шаблонов для просмотра.
   * @param {string} search
   * @param {string} categoryId
   * @returns {Promise<Either>}
   */
  this.viewList = (search = '', categoryId = '') => axios
    .get('/api/templates', { params: { search, category_id: categoryId } })
    .then(
      (response) => {
        const templates = response.data.data.map((item) => new Template(
          item.allowed_actions,
          item.id,
          item.is_sample,
          item.name,
          item.removing_id,
          item.type,
          item.type_name,
          item.file_name,
          item.category_id,
          item.is_esia_confirmation_required,
        ));
        return Either.of(templates);
      },
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
            || error.response.status === 403
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получить список всех шаблонов клиники, сгруппированных по категориям.
   *
   * @returns {Promise<Either>}
   */
  this.viewAllTemplatesListGroupByCategories = () => axios
    .get('/api/templates-by-categories')
    .then(
      (response) => {
        const categories = response.data.data.map((categoryData) => new CategoryWithTemplates(
          categoryData.category_id,
          categoryData.category_name,
          categoryData.templates.map((templateData) => new Template(
            templateData.allowed_actions,
            templateData.id,
            templateData.is_sample,
            templateData.name,
            templateData.removing_id,
            templateData.type,
            templateData.type_name,
            templateData.file_name,
            templateData.category_id,
          )),
        ));
        return Either.of(categories);
      },
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
            || error.response.status === 403
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получение всех шаблонных Пакетов для отображения в категории Пакеты документов
   * @return {*}
   */
  this.viewPackageList = (search) => axios
    .get('api/package-templates', { params: { search } })
    .then(
      (response) => {
        const packages = response.data.data.map((pkg) => {
          const attachedDocs = List.fromArray(
            (pkg.attached_documents || []).map(
              (doc) => new InternalDocument(
                doc.id,
                doc.name,
                doc.type,
                doc.is_sample,
              ),
            ),
          );
          return new Package(pkg.id, pkg.name, attachedDocs);
        });
        return Either.of(List.fromArray(packages));
      },
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
            || error.response.status === 403
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Создание нового шаблонного Пакета состоящего из документов/контрактов
   * @param request
   * @returns {*}
   */
  this.createPackageDocuments = (request) => axios
    .post('/api/package-templates', request)
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Получить список шаблонов по пациенту для просмотра.
   * @param {string} patientId
   * @param {string} search
   * @param {string} sort
   * @returns {Promise<Either>}
   */
  this.viewPatientList = (patientId, search = '', sort = '') => axios
    .get('/api/patient-templates', { params: { patient_id: patientId, search, sort } })
    .then(
      (response) => Either.of({
        list: response.data.data.list,
        patientUnderFifteen: response.data.data.patient_under_fifteen,
        isPatientPhoneExists: response.data.data.is_patient_phone_exists,
        isEmployeeLinked: response.data.data.is_employee_linked,
      }),
      (error) => {
        if (error.isAxiosError) {
          if (error.response && (error.response.status === 400 || error.response.status === 403)) {
            return Either.Left(error.response.data);
          }
          if (!error.response) {
            throw new Error('Network error: сервер по адресу /api/patient-templates не ответил на запрос');
          }
        }
        throw error;
      },
    );

  /**
   * @description Получить сгенерированный документ.
   * @param {number} templateId Идентификатор.
   * @returns {Promise<Validation>}
   */
  this.generate = (templateId) => axios
    .get(`/api/templates/${templateId}`)
    .then(async (response) => Validation.Success(response.data));

  /**
   * @description Получить категории документов.
   * @returns {Promise<Either>}
   */
  this.getDocumentCategories = async () => axios
    .get('api/templates/categories')
    .then(
      async (response) => Either.of(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const json = await (error.response.data || '').text();
          const data = JSON.parse(json);
          const { causes, title } = data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Создать категорию документа.
   * @param {DocumentCategoryCreateRequest} request
   * @returns {Promise<Either>}
   */
  this.createDocumentCategory = (request) => axios
    .post('api/templates/category', request.toQuery())
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Изменить категорию документа.
   * @param {DocumentCategoryEditRequest} request
   * @returns {Promise<Either>}
   */
  this.editDocumentCategory = (request) => axios
    .put(`api/templates/category/${request.getId()}`, request.toQuery())
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Удалить категорию документа.
   * @param {number} id
   * @returns {Promise<Either>}
   */
  this.deleteDocumentCategory = (id) => axios
    .delete(`api/templates/category/${id}`)
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
}
